<script setup>
import $ from 'jquery';
import { ref, onMounted, onBeforeUnmount, onUnmounted, watch, useTemplateRef } from 'vue';

const props = defineProps({
    select2Method: {
        type: String,
        default: 'jix_select2',
    },
    config: {
        type: Object,
        default() {
            return {};
        },
    },
    modelValue: {
        type: [String, Number, Array],
        default: null,
    },
    /**
     * When true, the select2 will not lock the scrolling of its parent modal
     */
    disableScrollBlock: {
        type: Boolean,
        default: false,
    },
    /**
     * When true, the select2's dropdown will be opened when unselecting an element
     */
    openWhenUnselecting: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'select', 'unselect', 'close']);

const selectElement = useTemplateRef('select');

const ignoreChanges = ref(false);

// Methods
function syncValue() {
    ignoreChanges.value = true;
    $(selectElement.value)
        .val(props.modelValue)
        .trigger('change');
    ignoreChanges.value = false;
}

/**
 * Focus the select2 and open the dropdown.
 */
function open() {
    $(selectElement.value).select2('open');
}

/**
 * Close the dropdown.
 */
function close() {
    $(selectElement.value).select2('close');
}

/**
 * Focus the select2 without opening the dropdown.
 */
function focus() {
    // instance.proxy.$el.focus() opens the dropdown.
    // $(instance.proxy.$el).select2('focus') makes it look like it is focused,
    // but keyboard focus is somewhere else.
    // This does the job:
    open();
    close();
}

function getEffectiveConfig() {
    const res = { ...props.config };
    if (props.disableScrollBlock) {
        res.dropdownParent = $(selectElement.value).parent();
    }
    return res;
}

function initializeSelect2() {
    const effectiveConfig = getEffectiveConfig();
    const element = $(selectElement.value)[props.select2Method](effectiveConfig)
        .val(props.modelValue)
        .trigger('change')
        .on('change', () => {
            if (!ignoreChanges.value) {
                emit('update:modelValue', $(selectElement.value).val());
            }
        })
        .on('select2:select', (event) => {
            if (!ignoreChanges.value) {
                emit('select', event.params.data);
            }
        })
        .on('select2:unselect', () => {
            if (!ignoreChanges.value) {
                emit('unselect');
            }
        })
        .on('select2:close', () => {
            if (!ignoreChanges.value) {
                emit('close');
            }
        });

    if (props.disableScrollBlock) {
        element.on('select2:open', function (e) {
            const event = "scroll.select2";
            $(e.target).parents().off(event);
            $(window).off(event);
        });
    }
    if (!props.openWhenUnselecting) {
        element.data('select2').$selection.on('click', '.select2-selection__choice__remove', function(e) {
            e.stopPropagation();
        });
    }
}

function destroySelect2(elem) {
    $(elem).off().select2('destroy');
}

// Lifecycle hooks
onMounted(initializeSelect2);

// We need to store the element to destroy, because in onUnmounted the
// selectElement ref is already null. We can't destroy in onBeforeUnmount
// because that seems to mess with Vue's element tracking and cause errors.
let destroyElement = null;
onBeforeUnmount(() => destroyElement = selectElement.value);
onUnmounted(() => destroySelect2(destroyElement));

// Watchers
watch(
    () => props.modelValue,
    () => syncValue(),
    { deep: true }
);

watch(
    () => props.config,
    () => {
        destroySelect2(selectElement.value);
        initializeSelect2();
    }
);

defineExpose({
    open,
    close,
    focus,
    syncValue
});
</script>

<template>
    <select ref="select">
        <slot />
    </select>
</template>
